/**
 * Blocos compartilhados das páginas locais de Bauru. Montadas com os mesmos
 * componentes das páginas de tratamento (Hero, Pilares, CardsIlustrado,
 * Medico, Faq, CtaPratico); aqui ficam só as seções de texto, o bloco de
 * endereço com mapa e a autoria, usando as classes já existentes do site.
 */
import type { ReactNode } from "react";

import { Markdown } from "@/components/blog/Markdown";
import { useAgendamentoLink } from "@/lib/itv/agendamento";
import { MAPS_EMBED, NAP } from "@/lib/itv/site";
import type { HeroProps } from "@/components/itv/tratamento/Hero";
import type { PilaresProps } from "@/components/itv/tratamento/Pilares";
import type { CardsIlustradoProps } from "@/components/itv/tratamento/CardsIlustrado";
import type { MedicoProps } from "@/components/itv/tratamento/Medico";
import type { FaqProps } from "@/components/itv/tratamento/Faq";
import type { CtaPraticoProps } from "@/components/itv/tratamento/CtaPratico";

/** Páginas locais têm apresentação própria, sem alterar os tratamentos. */
export function HeroLocal({ trilha, sobretitulo, linha1, linha2, chamada, botao = "Agendar avaliação" }: HeroProps) {
  const agendar = useAgendamentoLink();
  return (
    <>
      <section className="local-abertura">
        <div className="container">
          <nav className="local-trilha" aria-label="Você está em">
            <ol>{trilha.map((item, i) => <li key={item.nome}>{i > 0 ? <span aria-hidden="true">/</span> : null}{item.href ? <a href={item.href}>{item.nome}</a> : <span>{item.nome}</span>}</li>)}</ol>
          </nav>
          <p className="sobretitulo">{sobretitulo} · JARDIM EUROPA</p>
          <h1>{linha1} <em>{linha2}</em></h1>
          <p className="local-resposta">{chamada}</p>
          <div className="local-botoes">
            <a href={agendar} className="btn-ouro">{botao}<span aria-hidden="true">↗</span></a>
            <a href="/bauru/" className="local-endereco-link">Endereço e como chegar <span aria-hidden="true">→</span></a>
          </div>
        </div>
      </section>
      <div className="local-faixa-endereco">
        <div className="container">
          <img src="/icones/map-pin.svg" width={24} height={24} alt="" />
          <p><strong>{NAP.cidade}, {NAP.bairro}</strong><span>{NAP.logradouro}, {NAP.cidade}-{NAP.uf}, CEP {NAP.cep}</span></p>
          <a href="#consultorio">Ver consultório <span aria-hidden="true">↓</span></a>
        </div>
      </div>
    </>
  );
}

export function PilaresLocais({ sobretitulo, titulo, chamada, itens }: PilaresProps) {
  return <section className="local-servicos"><div className="container">
    <div className="local-secao-cabeca"><p className="sobretitulo">{sobretitulo}</p><h2>{titulo}</h2>{chamada ? <p className="local-intro">{chamada}</p> : null}</div>
    <ul className="local-servicos-grade">{itens.map(item => <li key={item.titulo}>
      <img src={item.icone} width={24} height={24} loading="lazy" alt="" />
      <h3>{item.titulo}</h3><p>{item.texto}</p>{item.link ? <a href={item.link.href}>{item.link.rotulo}</a> : null}
    </li>)}</ul>
  </div></section>;
}

export function EtapasLocais({ sobretitulo, titulo, chamada, cards }: CardsIlustradoProps) {
  return <section className="local-etapas"><div className="container">
    <div className="local-secao-cabeca"><p className="sobretitulo">{sobretitulo}</p><h2>{titulo}</h2>{chamada ? <p className="local-intro">{chamada}</p> : null}</div>
    <ol className="local-etapas-grade">{cards.map((card, index) => <li key={card.titulo}>
      <span className="local-etapa-numero" aria-hidden="true">{String(index + 1).padStart(2, "0")}</span>
      <div><h3>{card.titulo}</h3><p>{card.texto}</p></div>
    </li>)}</ol>
  </div></section>;
}

export function MedicoLocal({ paragrafos }: MedicoProps) {
  return <SecaoTexto titulo="Dr. Thiago Viana"><div className="local-medico-texto">{paragrafos.map((texto, i) => <p key={i}>{texto}</p>)}</div><a className="local-link" href="/sobre/">Conhecer o Dr. Thiago →</a></SecaoTexto>;
}

export function FaqLocal({ titulo, itens }: FaqProps) {
  return <section className="faq local-perguntas"><div className="container">
    <div className="faq-titulo"><p className="sobretitulo">DÚVIDAS SOBRE O ATENDIMENTO</p><h2>{titulo}</h2></div>
    {itens.map((item, i) => <details className="frow" key={item.pergunta}><summary><span className="frow-n">{String(i + 1).padStart(2, "0")}</span><h3>{item.pergunta}</h3><span className="frow-sinal" aria-hidden="true" /></summary><div className="frow-corpo"><p>{item.resposta}</p></div></details>)}
  </div></section>;
}

export function CtaLocal({ linha1, linha2 }: CtaPraticoProps) {
  const agendar = useAgendamentoLink();
  return <section className="local-fecho"><div className="container">
    <p className="sobretitulo">AGENDAMENTO</p><h2>{linha1} {linha2}</h2>
    <div className="local-botoes"><a className="btn-ouro" href={agendar}>Agendar pelo WhatsApp <span aria-hidden="true">↗</span></a><a href="/bauru/" className="local-endereco-link">Endereço e como chegar →</a></div>
  </div></section>;
}

export const RQES_LOCAIS = ["RQE 121770 · NUTROLOGIA", "RQE 118488 · MED. DO ESPORTE"];

export const AVISO = "Este conteúdo é educativo e não substitui consulta.";

/** Seção de texto corrido no ritmo das páginas de tratamento. */
export function SecaoTexto({
  titulo,
  children,
  areia = false,
  id,
}: {
  titulo?: string;
  children: ReactNode;
  areia?: boolean;
  id?: string;
}) {
  return (
    <section id={id} className={`local-secao${areia ? " local-secao--areia" : ""}${titulo ? " local-secao--dupla" : ""}`}>
      <div className="container local-secao-inner">
        {titulo ? (
          <div className="local-secao-cabeca">
            <h2>{titulo}</h2>
          </div>
        ) : null}
        <div className="local-secao-corpo">{children}</div>
      </div>
    </section>
  );
}

/** Parágrafos em markdown (links, negrito, tabela, vídeo) dentro da seção. */
export function Texto({ texto }: { texto: string }) {
  return (
    <div className="local-texto">
      <Markdown texto={texto} />
    </div>
  );
}

/** Endereço exato da ficha do Google, ao lado do mapa, como na /bauru/. */
export function MapaConsultorio({ avaliacoes = false }: { avaliacoes?: boolean }) {
  return (
    <div className="local-consultorio" id="consultorio">
      <div className="local-nap">
        <span className="local-nap-icone" aria-hidden="true">
          <img src="/icones/map-pin.svg" width={24} height={24} alt="" loading="lazy" />
        </span>
        <p>
          <strong>ITV Instituto Dr. Thiago Viana</strong>
        </p>
        <p>Av. Getúlio Vargas, 22-25, Sala 7, Torre 2, Jardim Europa, Bauru-SP, CEP 17017-383</p>
        <p>
          <a href={`tel:${NAP.telefoneE164}`}>{NAP.telefone}</a>
        </p>
        <p>
          <a className="local-link" href="/bauru/">
            Endereço e como chegar
          </a>
        </p>
        {avaliacoes ? (
          <p>
            <a className="local-link" href="https://maps.google.com/?cid=13509211634599351926" target="_blank" rel="noopener">
              Ver avaliações no Google
            </a>
          </p>
        ) : null}
      </div>
      <div className="local-mapa">
        <iframe
          src={MAPS_EMBED}
          title={`Mapa do consultório em ${NAP.cidade}`}
          width="600"
          height="450"
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          allowFullScreen
        />
      </div>
    </div>
  );
}

export function BotaoWhatsApp({ rotulo }: { rotulo: string }) {
  const agendar = useAgendamentoLink();
  return (
    <div className="local-botoes">
      <a className="btn-ouro" href={agendar}>
        {rotulo}
      </a>
    </div>
  );
}

/** Assinatura do autor, no mesmo bloco do blog. */
export function Autoria() {
  return (
    <section className="artigo-autor local-autor">
      <div className="artigo-autor-conteudo">
        <p className="artigo-autor-rotulo t-label-block uppercase">MÉDICO</p>
        <p className="artigo-autor-nome t-heading-sm">Dr. Thiago Viana</p>
        <p className="artigo-autor-crm t-card-meta">CRM-SP 144.585</p>
        <p className="artigo-autor-bio t-card-excerpt">Nutrologia RQE 121770 · Medicina do Esporte RQE 118488</p>
        <p className="artigo-autor-crm t-card-meta">Atualizado em 16/10/2026</p>
        <a href="/sobre/" className="artigo-autor-link t-link-inline inline-flex items-center">
          Conheça o Dr. Thiago →
        </a>
      </div>
    </section>
  );
}

/** Autoria seguida do aviso médico, como no blog. */
export function AutoriaComAviso() {
  return (
    <section className="local-secao">
      <div className="container local-secao-inner">
        <Autoria />
        <p className="local-aviso t-card-excerpt">{AVISO}</p>
      </div>
    </section>
  );
}
